import Tabs from "./tabs";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formatDate } from "../../../components/formatDateAndTime/formatDateAndTime";
import { Box, Stack, Typography, Avatar, Tooltip } from "@mui/material";
import DescriptionIcon from '@mui/icons-material/Description';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import SyncIcon from '@mui/icons-material/Sync';
import { FieldSelect } from "../../../components/selections/fieldInputs";
import { QR, QrActionButton } from "../../qrCode/components/qrUi";
import SnackbarMessage from "../../../components/snackBar/snackBar";
import { deleteTemplateThunk, getTemplateThunk } from "../templatesThunk";
import DataTableWithCheckbox from "../../../components/dataTableWithSearch/DataTableWithCheckBox";
import ViewTemplateModal from "./viewTemplateModal";

export default function TemplatesList() {
    const dispatch = useDispatch();

    const { getTemplates, status, error } = useSelector((state) => state.templates);

    const filterData = [
        { value: 1, label: 'All' },
        { value: 2, label: 'Approved' },
        { value: 3, label: 'Pending' },
        { value: 4, label: 'Rejected' },
    ];

    const [state, setState] = useState({
        selectFilterOption: 1,
        viewTemplate: false,
        selectedTemplateRow: {},
    });

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const columns = useMemo(() => [
        { header: "SNO", accessorKey: "sno", enableGlobalFilter: true },
        {
            accessorKey: "template_name", header: "Template Name",
            Cell: ({ row }) => (
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.25}
                    title="View template"
                    onClick={() => setState(prev => ({ ...prev, viewTemplate: true, selectedTemplateRow: row.original }))}
                    sx={{
                        cursor: "pointer",
                        // clickable: always blue + underlined (darker on hover)
                        "& .tpl-name": { color: "#2563eb !important", textDecoration: "underline", textDecorationColor: "#2563eb66", textUnderlineOffset: "3px" },
                        "&:hover .tpl-name": { color: "#1d4ed8 !important", textDecorationColor: "#1d4ed8" },
                        "&:hover .tpl-icon": { bgcolor: QR.blue, color: "#fff" },
                    }}
                >
                    <Avatar className="tpl-icon" variant="rounded" sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: `${QR.main}14`, color: QR.main, transition: "all .15s ease" }}>
                        <DescriptionIcon sx={{ fontSize: 17 }} />
                    </Avatar>
                    <Typography className="tpl-name" variant="body2" fontWeight={700} color="#0f172a" noWrap>
                        {row.original.template_name}
                    </Typography>
                </Stack>
            )
        },
        {
            accessorKey: "template_lang",
            header: "Language",
            Cell: ({ row }) =>
                row.original.template_lang ? (
                    <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: 1.5, fontFamily: "monospace", fontSize: 12, color: "#334155", bgcolor: "#f1f5f9", border: "1px solid #e2e8f0" }}>
                        {row.original.template_lang}
                    </Box>
                ) : "-",
        },
        {
            accessorKey: "template_category",
            header: "Category",
            Cell: ({ row }) =>
                row.original.template_category ? (
                    <Box component="span" sx={{ px: 1, py: 0.3, borderRadius: 5, fontSize: 12, fontWeight: 700, textTransform: "capitalize", color: QR.purple, bgcolor: `${QR.purple}12`, border: `1px solid ${QR.purple}26` }}>
                        {String(row.original.template_category).toLowerCase()}
                    </Box>
                ) : "-",
        },
        {
            accessorKey: "status",
            header: "Status",
            Cell: ({ row }) => {
                const status = row.original.status || "";
                const colorMap = {
                    approved: "#16a34a",
                    rejected: "#dc2626",
                    pending: "#d97706",
                };
                const c = colorMap[status.toLowerCase()] || "#475569";

                // Soft status chip: dot + label
                return (
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, px: 1, py: 0.3, borderRadius: 5, fontSize: 12, fontWeight: 700, textTransform: "capitalize", whiteSpace: "nowrap", color: c, bgcolor: `${c}14`, border: `1px solid ${c}33` }}>
                        <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: c }} />
                        {status.toLowerCase()}
                    </Box>
                );
            },
        },
        {
            accessorKey: "inserted_time",
            header: "Created On",
            Cell: ({ row }) => (
                <Typography variant="body2" color="text.secondary">{formatDate(row.original.inserted_time)}</Typography>
            ),
        }
    ], []);

    // useEffect(() => {
    //     if (status === "failed") {
    //         setSnackbar({
    //             open: true,
    //             message: error,
    //             severity: "error",
    //         });
    //     }
    // }, [status, error]);

    const handleDeleteTemplate = async (id) => {
        try {
            const response = await dispatch(deleteTemplateThunk({ credentials: { template_id: id } }));
            if (response.meta.requestStatus === "fulfilled") {
                setSnackbar({ open: true, message: response.payload.message, severity: "success" });
                dispatch(getTemplateThunk({ type: "getTemplate" }));
                setState(prev => ({ ...prev, viewTemplate: false }))
            } else {
                setSnackbar({ open: true, message: response.payload?.message || "Failed to delete template.", severity: "error" });
                setState(prev => ({ ...prev, viewTemplate: false }))
            }
        } catch (error) {
            console.error(error);
            setSnackbar({ open: true, message: "Something went wrong.", severity: "error" });
        }
    };

    const handleSingleSelectChange = (name, selectedValue) => {
        setState((prev) => ({
            ...prev,
            [name]: selectedValue,
            errors: { ...prev.errors, [name]: "" },
        }));
    };

    // Sync: refetch templates; the server also refreshes Pending templates' approval status from WhatsApp
    const syncTemplates = () => {
        dispatch(getTemplateThunk({ type: "getTemplate" }));
    };

    const renderTemplateFilter = () => {
        return (
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 2 }}>
                <Box sx={{ width: { xs: "100%", sm: 240 } }}>
                    <FieldSelect
                        name="selectFilterOption"
                        label="Status"
                        options={filterData}
                        value={state.selectFilterOption}
                        onChange={handleSingleSelectChange}
                        icon={<FilterListIcon />}
                        placeholder="All"
                    />
                </Box>
                <Tooltip title="Fetch the latest templates and update Pending approval status from WhatsApp" placement="top" arrow>
                    <Box sx={{ ml: { sm: "auto" }, width: { xs: "100%", sm: "auto" }, "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } } }}>
                        <QrActionButton
                            icon={<SyncIcon sx={{ fontSize: 18 }} />}
                            label="Sync"
                            loading={status === "loading"}
                            loadingLabel="Syncing..."
                            onClick={syncTemplates}
                        />
                    </Box>
                </Tooltip>
            </Box>
        )
    };


    const filteredTemplateDetails =
        state.selectFilterOption === 1
            ? getTemplates
            : getTemplates.filter((item) => {
                const filterLabel = filterData.find(
                    (nd) => nd.value === state.selectFilterOption
                )?.label;
                return (
                    filterLabel &&
                    item.status.toLowerCase() === filterLabel.toLowerCase()
                );
            });


    // Summary cards (display only, from the loaded templates)
    const all = getTemplates || [];
    const countStatus = (st) => all.filter((t) => String(t.status || "").toLowerCase() === st).length;
    const stats = [
        { label: "Templates", value: all.length, icon: <DescriptionIcon />, color: QR.main },
        { label: "Approved", value: countStatus("approved"), icon: <TaskAltIcon />, color: "#16a34a" },
        { label: "Pending", value: countStatus("pending"), icon: <HourglassTopIcon />, color: QR.amber },
        { label: "Rejected", value: countStatus("rejected"), icon: <CancelOutlinedIcon />, color: "#dc2626" },
    ];

    return (
        <div>
            <Tabs stats={stats}>
                <DataTableWithCheckbox
                    columns={columns}
                    data={filteredTemplateDetails || []}
                    enableRowSelection={false}
                    renderFilters={renderTemplateFilter()}
                    status={status}
                    searchFirst
                    emptyMessage={state.selectFilterOption === 1 ? "No templates yet. Create one to get started." : "No templates with this status."}
                />
            </Tabs>

            <ViewTemplateModal show={state.viewTemplate} handleClose={() => setState(prev => ({ ...prev, viewTemplate: false }))} handleDeleteTemplate={handleDeleteTemplate} rowData={state.selectedTemplateRow} />
            <SnackbarMessage open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} />
        </div>
    );
}
